import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/admin/AdminLayout';
import { PageLoader } from './components/ui';

// ── Public pages ───────────────────────────────────────────────
const LandingPage = React.lazy(() => import('./pages/auth/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));

// ── App pages ──────────────────────────────────────────────────
const SuperAdminDashboard = React.lazy(() => import('./pages/dashboard/SuperAdminDashboard'));
const FarmManagerDashboard = React.lazy(() => import('./pages/dashboard/FarmManagerDashboard'));
const EmployeeDashboard = React.lazy(() => import('./pages/dashboard/EmployeeDashboard'));
const UsersPage = React.lazy(() => import('./pages/users/UsersPage'));
const FarmsPage = React.lazy(() => import('./pages/farms/FarmsPage'));
const EggCollectionPage = React.lazy(() => import('./pages/eggs/EggCollectionPage'));
const SalesPage = React.lazy(() => import('./pages/sales/SalesPage'));
const CustomersPage = React.lazy(() => import('./pages/customers/CustomersPage'));
const ReceivablesPage = React.lazy(() => import('./pages/receivables/ReceivablesPage'));
const CreditorsPage = React.lazy(() => import('./pages/creditors/CreditorsPage'));
const ProfitLossPage = React.lazy(() => import('./pages/reports/ProfitLossPage'));
const ActivitiesPage = React.lazy(() => import('./pages/operations/index').then(m => ({ default: m.ActivitiesPage })));
const InventoryPage = React.lazy(() => import('./pages/operations/index').then(m => ({ default: m.InventoryPage })));
const BirdsPage = React.lazy(() => import('./pages/birds/BirdsPage'));
const BirdHealthChartPage = React.lazy(() => import('./pages/birds/BirdHealthChartPage'));
const ExpensesPage = React.lazy(() => import('./pages/operations/index').then(m => ({ default: m.ExpensesPage })));
const ProfilePage = React.lazy(() => import('./pages/settings/ProfilePage'));
const ChangePasswordPage = React.lazy(() => import('./pages/settings/ChangePasswordPage'));

// ── Subscription pages ─────────────────────────────────────────
const SubscriptionPage = React.lazy(() => import('./pages/subscription/SubscriptionPage'));
const VerifyPage = React.lazy(() => import('./pages/subscription/VerifyPage'));
const SubscriptionWall = React.lazy(() => import('./pages/subscription/SubscriptionWall'));

const EggPricingPage = React.lazy(() => import('./pages/settings/EggPricingPage'));

// ── Platform admin pages ────────────────────────────────────────
const AdminLoginPage = React.lazy(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboardPage = React.lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminAnalyticsPage = React.lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminDormantUsersPage = React.lazy(() => import('./pages/admin/AdminDormantUsersPage'));
const AdminBroadcastPage = React.lazy(() => import('./pages/admin/AdminBroadcastPage'));
const AdminFarmsPage = React.lazy(() => import('./pages/admin/AdminFarmsPage'));
const AdminFarmDetailPage = React.lazy(() => import('./pages/admin/AdminFarmDetailPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminSubscriptionsPage = React.lazy(() => import('./pages/admin/AdminSubscriptionsPage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000, refetchOnWindowFocus: false } },
});

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  roles?: string[];
  requireSub?: boolean;
}> = ({ children, roles, requireSub = true }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { isActive, isGrace, isLoading: subLoading, subscription } = useSubscription();

  if (isLoading || (requireSub && subLoading)) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;

  if (requireSub && user?.role === 'super_admin' && !isActive && !isGrace) {
    // No subscription ever → go subscribe. Expired subscription → go to expired wall.
    const destination = subscription ? '/subscription/expired' : '/subscription';
    return <Navigate to={destination} replace />;
  }

  return <>{children}</>;
};

const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  if (!user) return <PageLoader />;
  if (user.role === 'super_admin') return <SuperAdminDashboard />;
  if (user.role === 'farm_manager') return <FarmManagerDashboard />;
  return <EmployeeDashboard />;
};

const wrap = (Component: React.ComponentType) => (
  <DashboardLayout>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </DashboardLayout>
);

// ── Platform admin — fully separate session/layout from the tenant app ──
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAdminAuth();
  if (isLoading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  return <>{children}</>;
};

const adminWrap = (Component: React.ComponentType) => (
  <AdminLayout>
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  </AdminLayout>
);

const AdminApp: React.FC = () => (
  <AdminAuthProvider>
    <Routes>
      <Route path="login" element={<Suspense fallback={<PageLoader />}><AdminLoginPage /></Suspense>} />
      <Route path="dashboard" element={<AdminProtectedRoute>{adminWrap(AdminDashboardPage)}</AdminProtectedRoute>} />
      <Route path="analytics" element={<AdminProtectedRoute>{adminWrap(AdminAnalyticsPage)}</AdminProtectedRoute>} />
      <Route path="farms" element={<AdminProtectedRoute>{adminWrap(AdminFarmsPage)}</AdminProtectedRoute>} />
      <Route path="farms/:farmId" element={<AdminProtectedRoute>{adminWrap(AdminFarmDetailPage)}</AdminProtectedRoute>} />
      <Route path="users" element={<AdminProtectedRoute>{adminWrap(AdminUsersPage)}</AdminProtectedRoute>} />
      <Route path="dormant-users" element={<AdminProtectedRoute>{adminWrap(AdminDormantUsersPage)}</AdminProtectedRoute>} />
      <Route path="subscriptions" element={<AdminProtectedRoute>{adminWrap(AdminSubscriptionsPage)}</AdminProtectedRoute>} />
      <Route path="broadcast" element={<AdminProtectedRoute>{adminWrap(AdminBroadcastPage)}</AdminProtectedRoute>} />
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  </AdminAuthProvider>
);

const AppShell: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><PageLoader /></div>;

  return (
    <Routes>
      {/* ── Public ───────────────────────────────────────────── */}
      <Route path="/" element={<Suspense fallback={<PageLoader />}><LandingPage /></Suspense>} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />

      {/* ── Subscription (no subscription required) ──────────── */}
      <Route path="/subscription/verify" element={
        <ProtectedRoute requireSub={false}>
          <Suspense fallback={<PageLoader />}><VerifyPage /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/subscription/expired" element={
        <ProtectedRoute requireSub={false}>
          <Suspense fallback={<PageLoader />}><SubscriptionWall /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute requireSub={false} roles={['super_admin']}>
          {wrap(SubscriptionPage)}
        </ProtectedRoute>
      } />

      {/* ── Protected app routes ─────────────────────────────── */}
      <Route path="/dashboard" element={<ProtectedRoute>{wrap(DashboardRouter)}</ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute roles={['super_admin', 'farm_manager']}>{wrap(UsersPage)}</ProtectedRoute>} />
      <Route path="/farms" element={<ProtectedRoute roles={['super_admin']}>{wrap(FarmsPage)}</ProtectedRoute>} />
      <Route path="/eggs" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'employee', 'accountant']}>{wrap(EggCollectionPage)}</ProtectedRoute>} />
      <Route path="/birds" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'employee', 'accountant']}>{wrap(BirdsPage)}</ProtectedRoute>} />
      <Route path="/birds/health-chart" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'employee', 'accountant']}>{wrap(BirdHealthChartPage)}</ProtectedRoute>} />
      <Route path="/activities" element={<ProtectedRoute>{wrap(ActivitiesPage)}</ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'sales_officer']}>{wrap(SalesPage)}</ProtectedRoute>} />
      <Route path="/sales/new" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'sales_officer']}>{wrap(SalesPage)}</ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'sales_officer']}>{wrap(CustomersPage)}</ProtectedRoute>} />
      <Route path="/receivables" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'sales_officer']}>{wrap(ReceivablesPage)}</ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute>{wrap(InventoryPage)}</ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'accountant']}>{wrap(ExpensesPage)}</ProtectedRoute>} />
      <Route path="/profit-loss" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'accountant']}>{wrap(ProfitLossPage)}</ProtectedRoute>} />
      <Route path="/creditors" element={<ProtectedRoute roles={['super_admin', 'farm_manager', 'accountant']}>{wrap(CreditorsPage)}</ProtectedRoute>} />

      <Route path="/settings/egg-pricing" element={<ProtectedRoute roles={['super_admin']}>{wrap(EggPricingPage)}</ProtectedRoute>} />

      <Route path="/settings/profile" element={<ProtectedRoute>{wrap(ProfilePage)}</ProtectedRoute>} />
      <Route path="/settings/password" element={<ProtectedRoute>{wrap(ChangePasswordPage)}</ProtectedRoute>} />

      {/* ── Platform admin (separate session, separate login) ─── */}
      <Route path="/admin/*" element={<AdminApp />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <SubscriptionProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: { fontSize: '13px', borderRadius: '10px', border: '1px solid #e5e7eb' },
              success: { iconTheme: { primary: '#16a34a', secondary: '#fff' } },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
            }}
          />
        </BrowserRouter>
      </SubscriptionProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;